/**
 * Guide CDN cookie session (#1566, epic #1563).
 *
 * The guide-content CDN (guides.budgetrecon.com) is gated by CloudFront signed
 * cookies (#1567 flips enforcement). This module keeps the browser's cookie
 * triple fresh by calling the JWT-protected GET /api/guides/session endpoint
 * (#1565) ahead of CDN reads.
 *
 * Contract:
 *   - fast no-op while cookies are fresh (refresh-ahead margin below the
 *     server TTL, computed on the client clock only);
 *   - concurrent callers share one in-flight request;
 *   - never throws: resolves true when cookies are (still) fresh or the
 *     refresh succeeded, false when the refresh failed. Callers treat false
 *     as "the next CDN fetch may 403" and rely on their own retry semantics.
 *
 * credentials: 'include' is required on the SESSION call itself: without it
 * the browser will not STORE the cross-origin Set-Cookie response headers.
 * apiGlobal takes no fetch options, so this builds on authenticatedFetch.
 */

import { authenticatedFetch } from '../services/authService';
import { getApiUrl } from '../utils/config';

/** Refresh this many seconds before the server-reported TTL expires. */
const REFRESH_MARGIN_SECONDS = 300;

let refreshAt = 0; // epoch ms after which cookies need a refresh
let inFlight = null; // shared promise deduping concurrent refreshes

export const __resetForTests = () => {
  refreshAt = 0;
  inFlight = null;
};

/**
 * Ensure the CDN cookie triple is fresh.
 * @param {{force?: boolean}} [opts] force bypasses the freshness window
 *   (used for the one 403-recovery retry, spec R11 transport carve-out).
 * @returns {Promise<boolean>} true = fresh/refreshed, false = refresh failed.
 */
export const ensureCdnCookies = async ({ force = false } = {}) => {
  if (!force && Date.now() < refreshAt) return true;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await authenticatedFetch(`${getApiUrl()}/guides/session`, {
        credentials: 'include',
      });
      if (!res.ok) {
        console.warn('guide CDN cookie refresh failed:', res.status);
        return false;
      }
      const body = await res.json();
      const ttl = typeof body.ttlSeconds === 'number' ? body.ttlSeconds : 0;
      refreshAt = Date.now() + Math.max(ttl - REFRESH_MARGIN_SECONDS, 0) * 1000;
      return true;
    } catch (err) {
      console.warn('guide CDN cookie refresh failed:', err && err.message);
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};
